import { PERMISSION } from '~/app/enums/Permission';

export type ProfileReviewAccess = 'pending' | 'granted' | 'denied';

/** Profile review lives in Visibility > My trades: it needs both, as the sidebar and the tabs grant them. */
export const PROFILE_REVIEW_PERMISSIONS = [PERMISSION.MARKETPLACE_ACCESS, PERMISSION.MARKETPLACE_WORKFIELDS] as const;

/**
 * Same rule as the Visibility sidebar entry and its tabs: a key hidden by the user's role (`isMenuEntryDenied`)
 * or gated by the plan (`isUpsellRestricted`) closes Profile review. Until the menu-visibility map is loaded
 * nothing is granted: both helpers answer "allowed" by default, which let every user load the reviews.
 */
export default function useProfileReviewAccess() {
    const menuVisibility = useMenuVisibilityStore();
    const { isMenuEntryDenied, isUpsellRestricted } = useUpsellAccess();

    const access = computed<ProfileReviewAccess>(() => {
        if (!menuVisibility.loaded) return 'pending';
        return PROFILE_REVIEW_PERMISSIONS.every((key) => !isMenuEntryDenied(key) && !isUpsellRestricted(key)) ? 'granted' : 'denied';
    });

    const isChecking = computed(() => !menuVisibility.loaded && menuVisibility.loading);

    // Role denial, as opposed to a plan restriction that the route guard already turns into an inline upsell.
    const isUserDenied = computed(() => menuVisibility.loaded && PROFILE_REVIEW_PERMISSIONS.some((key) => isMenuEntryDenied(key)));

    return { access, isChecking, isUserDenied };
}
